//src/pages/pre-built/user-manage/MedicalPartner.js
import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import moment from "moment";
import swal from "sweetalert";

import { Modal, ModalBody } from "reactstrap";
import { Icon } from "../../../components/Component";
import { UserContext } from "./UserContext";
import http from "../../../utils/http-common";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { useMedicalPartnerListSelection } from "../../../hooks/useMedicalPartnerListSelection";
import ConfirmModal from "../../../components/partials/user-manage/ConfirmModal";
import PartnerStatsCards from "../../../components/partials/user-manage/PartnerStatsCards";
import MedicalPartnerDetails from "./MedicalPartnerDetails";
import MedicalPartnerListTabs from "../../../components/features/medical-partner-admin/MedicalPartnerListTabs";
import MedicalPartnerListToolbar from "../../../components/features/medical-partner-admin/MedicalPartnerListToolbar";
import MedicalPartnerListBulkBar from "../../../components/features/medical-partner-admin/MedicalPartnerListBulkBar";
import MedicalPartnerListTable from "../../../components/features/medical-partner-admin/MedicalPartnerListTable";
import AdminPageHeader from "../../../components/layouts/AdminPageHeader";
import AdminPageColumn from "../../../components/layouts/AdminPageColumn";
import AdminToolbarToggle from "../../../components/layouts/AdminToolbarToggle";
import DataTableShell from "../../../components/layouts/DataTableShell";
import MedicalPartnerAssignPrimaryUserModal from "../../../components/features/medical-partner-admin/MedicalPartnerAssignPrimaryUserModal";
import MedicalPartnerCreateModal from "../../../components/features/medical-partner-admin/MedicalPartnerCreateModal";
import { getPublicUrl } from "../../../utils/Utils";
import { meCan } from "../../../utils/adminPermissions";
import {
  MEDICAL_PARTNER_STATUS_FILTER_OPTIONS,
  MEDICAL_PARTNER_TC_FILTER_OPTIONS,
  buildCountryFilterOptionsFromPartners,
  normalizeTcApprovalStatus,
  partnerOffersMedicalTourism,
} from "../../../components/features/medical-partner-admin/medicalPartnerAdminConstants";
import {
  filterUserEligibleAsNewMedicalPartnerPrimary,
  partnerWorkflowStatus,
} from "../../../components/features/medical-partner-admin/medicalPartnerListTableHelpers";
import {
  approveMedicalPartnerAsLive,
  runPartnerWorkflowTransition,
} from "../../../components/features/medical-partner-admin/partnerWorkflowActions";

const MedicalPartner = () => {
  const history = useHistory();
  const canDeletePartner = meCan("directory.partner_delete.act");
  const { contextData } = useContext(UserContext);
  const [data, setData] = contextData;
  const [sm, updateSm] = useState(false);
  const [onSearchText, setSearchText] = useState("");
  const [modal, setModal] = useState({ add: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [createPartnerBusy, setCreatePartnerBusy] = useState(false);
  const [errorVal, setError] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // "all" | "pending" | "active"
  const [showConfirmModal, setShowConfirmModal] = useState({
    isOpen: false,
    type: null, // "approve" | "deletePartner"
    partner: null,
    onConfirm: null,
  });
  const [filters, setFilters] = useState({
    status: "",
    country: "",
    date: "",
    tcApproval: "",
  });
  // http-common handles auth and API base URL

  const [detailRefreshKey, setDetailRefreshKey] = useState(0);
  const [dossierModalPartnerId, setDossierModalPartnerId] = useState(null);
  const [assignPartnerId, setAssignPartnerId] = useState(null);
  const [patchBusyPartnerId, setPatchBusyPartnerId] = useState(null);
  const [tcBusyId, setTcBusyId] = useState(null);
  const [statusBusyId, setStatusBusyId] = useState(null);

  const [clinicsPending, setClinicsPending] = useState(0);
  const [bulkBusy, setBulkBusy] = useState(false);
  const {
    selectedPartnerIds,
    togglePartnerRowSelected,
    toggleSelectAllOnPage,
    clearPartnerSelection,
  } = useMedicalPartnerListSelection(activeTab);

  const listUiBusy = loading || createPartnerBusy;

  const syncPartners = useCallback(() => {
    return http
      .get("/api/v1/medical-partners")
      .then((response) => {
        const partners = response.data?.data || response.data || [];
        const cleanData = partners.filter((item) => item && item.organization_name);
        setData(cleanData);
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        console.error("Error refreshing partners:", error);
      });
  }, [setData]);

  const patchPartnerListField = useCallback(
    async (partnerId, payload) => {
      if (!partnerId) return;
      setPatchBusyPartnerId(String(partnerId));
      try {
        await http.put(`/api/v1/medical-partners/${partnerId}`, payload);
        await syncPartners();
        if (dossierModalPartnerId && String(dossierModalPartnerId) === String(partnerId)) {
          setDetailRefreshKey((k) => k + 1);
        }
      } catch (e) {
        swal("Error", e.response?.data?.message || "Update failed", "error");
      } finally {
        setPatchBusyPartnerId(null);
      }
    },
    [syncPartners, dossierModalPartnerId]
  );

  const onTcSwitchChange = async (item, wantApproved) => {
    if (!item?._id) return;
    const id = item._id;
    setTcBusyId(id);
    try {
      if (wantApproved) {
        await http.put(`/api/v1/medical-partners/${id}`, {
          tcApprovalStatus: "approved",
          tcApprovalNote: item.tcApprovalNote || "",
          tcApprovalUpdatedAt: new Date().toISOString(),
        });
        await syncPartners();
        swal("Success", "Teleconsultation status updated", "success");
        return;
      }
      const ok = await swal({
        title: "Clear partner TC approval?",
        text: "Sets teleconsultation platform status to Off.",
        icon: "warning",
        buttons: ["Cancel", "Clear approval"],
        dangerMode: true,
      });
      if (!ok) return;
      await http.put(`/api/v1/medical-partners/${id}`, {
        tcApprovalStatus: "off",
        tcApprovalNote: item.tcApprovalNote || "",
        tcApprovalUpdatedAt: new Date().toISOString(),
      });
      await syncPartners();
      swal("Success", "Teleconsultation status updated", "success");
    } catch (error) {
      swal("Error", error.response?.data?.message || "Update failed", "error");
    } finally {
      setTcBusyId(null);
    }
  };

  const onStatusSelect = (item, nextStatus) => {
    if (!item?._id) return;
    const current = partnerWorkflowStatus(item);
    if (current === nextStatus) return;
    const id = item._id;
    setStatusBusyId(id);
    void (async () => {
      try {
        const applied = await runPartnerWorkflowTransition(item, nextStatus, { http, swal });
        if (applied) {
          await syncPartners();
          if (dossierModalPartnerId && String(dossierModalPartnerId) === String(id)) {
            setDetailRefreshKey((k) => k + 1);
          }
          const msg =
            nextStatus === "active" &&
            (current === "pending" || current === "inactive" || current === "draft")
              ? "Partner set to Live"
              : "Partner status updated";
          swal("Success", msg, "success");
        }
      } catch (e) {
        swal("Error", e?.response?.data?.message || e?.message || "Update failed", "error");
      } finally {
        setStatusBusyId(null);
      }
    })();
  };

  const onMtSwitchChange = useCallback(
    (item, checked) => {
      if (!item?._id) return;
      if (partnerOffersMedicalTourism(item) === checked) return;
      patchPartnerListField(item._id, { offersMedicalTourism: checked });
    },
    [patchPartnerListField]
  );

  useEffect(() => {
    const ac = new AbortController();
    http
      .get("/api/v1/medical-partners", { signal: ac.signal })
      .then((response) => {
        const partners = response.data?.data || response.data || [];
        const cleanData = partners.filter((item) => item && item.organization_name);
        setData(cleanData);
      })
      .catch((error) => {
        if (isAbortError(error)) return;
        console.error("Error loading data:", error);
        setError(error.response?.data?.message || "Failed to load medical partners");
      });
    return () => ac.abort();
  }, [setData]);

  const debouncedSearchText = useDebouncedValue(onSearchText, 300);

  const allPartners = useMemo(
    () => (Array.isArray(data) ? data : []).filter((item) => item && item.organization_name),
    [data]
  );

  const matchesStatusFacet = (item, filterVal) => {
    if (!filterVal) return true;
    const s = String(item.status || "");
    if (filterVal === "Active") return s === "active" || s === "Active";
    if (filterVal === "Pending") return s === "pending" || s === "Pending";
    if (filterVal === "Suspend") {
      return s === "Suspend" || s === "Suspended" || s === "inactive" || s === "draft";
    }
    return s === filterVal;
  };

  const partnersAfterSearchAndFacets = useMemo(() => {
    let list = allPartners;
    const q = debouncedSearchText.trim().toLowerCase();
    if (q) {
      list = list.filter((item) => {
        const searchFields = [
          item?.organization_name,
          item?.contact_person_name,
          item?.contact_person_email,
          item?.contact_person_phone,
          item?.landing_slug,
          item?.slug,
        ];
        return searchFields.some((field) => field && String(field).toLowerCase().includes(q));
      });
    }
    if (filters.status) {
      list = list.filter((item) => matchesStatusFacet(item, filters.status));
    }
    if (filters.country) {
      list = list.filter((item) => item.contact_person_country === filters.country);
    }
    if (filters.date) {
      list = list.filter(
        (item) => item.createdAt && moment(item.createdAt).format("YYYY-MM-DD") === filters.date
      );
    }
    if (filters.tcApproval) {
      list = list.filter((item) => normalizeTcApprovalStatus(item) === filters.tcApproval);
    }
    return list;
  }, [allPartners, debouncedSearchText, filters]);

  const filteredPartners = useMemo(() => {
    if (activeTab === "pending") {
      return partnersAfterSearchAndFacets.filter(
        (p) => p.status === "pending" || p.status === "Pending"
      );
    }
    if (activeTab === "active") {
      return partnersAfterSearchAndFacets.filter(
        (p) => p.status === "active" || p.status === "Active"
      );
    }
    return partnersAfterSearchAndFacets;
  }, [partnersAfterSearchAndFacets, activeTab]);

  const assignPartnerRecord = useMemo(() => {
    if (!assignPartnerId) return null;
    return allPartners.find((p) => String(p._id) === String(assignPartnerId)) || null;
  }, [assignPartnerId, allPartners]);

  useEffect(() => {
    if (assignPartnerId && !assignPartnerRecord) {
      setAssignPartnerId(null);
    }
  }, [assignPartnerId, assignPartnerRecord]);

  const hasListFilters =
    Boolean(debouncedSearchText.trim()) ||
    Boolean(filters.status) ||
    Boolean(filters.country) ||
    Boolean(filters.date) ||
    Boolean(filters.tcApproval);

  const statusOptions = MEDICAL_PARTNER_STATUS_FILTER_OPTIONS;
  const tcApprovalOptions = MEDICAL_PARTNER_TC_FILTER_OPTIONS;
  const countryOptions = useMemo(() => buildCountryFilterOptionsFromPartners(data), [data]);

  const openAddPartnerModal = () => setModal({ add: true });

  const onFilterChange = (e, type) => {
    if (type) {
      const value = type === "date" ? e.target.value : e?.value || "";
      setFilters((prev) => ({
        ...prev,
        [type]: value,
      }));
    } else {
      setSearchText(e.target.value);
    }
  };

  const clearAllListFilters = () => {
    setSearchText("");
    setFilters({
      status: "",
      country: "",
      date: "",
      tcApproval: "",
    });
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText, filters, activeTab]);

// Fonction de suppression
const onDeleteClick = async (id) => {
  try {
    const partnerToDelete = allPartners.find((item) => item._id === id);
    if (!partnerToDelete) {
      swal("Error", "Partner not found", "error");
      return;
    }

    setShowConfirmModal({
      isOpen: true,
      type: "deletePartner",
      partner: partnerToDelete,
      onConfirm: () => {
        deletePartner(id);
        setShowConfirmModal({ isOpen: false, type: null, partner: null, onConfirm: null });
      },
    });
  } catch (error) {
    console.error('Full error:', error);
    console.error('Error details:', error.response?.data);
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        "Failed to delete partner";
    swal("Error", errorMessage, "error");
  }
};

const deletePartner = async (id) => {
  try {
    const partnerToDelete = allPartners.find((item) => item._id === id);
    if (!partnerToDelete) {
      swal("Error", "Partner not found", "error");
      return;
    }

    setLoading(true);
    setError("");

    const partnerId = partnerToDelete._id || partnerToDelete.id || id;
    
    http
      .delete(`/api/v1/medical-partners/${partnerId}`)
      .then((response) => {
        console.log("Medical partner deleted:", response.data);
        return http.get("/api/v1/medical-partners");
      })
      .then((response) => {
        const partners = response.data?.data || response.data || [];
        const cleanData = partners.filter(item => item && item.organization_name);
        setData(cleanData);
        setLoading(false);
        swal("Success", "Partner deleted successfully", "success");
      })
      .catch((error) => {
        console.error("Error deleting medical partner:", error);
        const errorMessage = error.response?.data?.message || error.message || "Failed to delete partner";
        setError(errorMessage);
        swal("Error", errorMessage, "error");
        setLoading(false);
      });
  } catch (error) {
    console.error('Full error:', error);
    console.error('Error details:', error.response?.data);
    const errorMessage = error.response?.data?.message || 
                        error.message || 
                        "Failed to delete partner";
    swal("Error", errorMessage, "error");
    setLoading(false);
  }
};

  const approvePartner = async (partnerId) => {
    setLoading(true);
    try {
      await approveMedicalPartnerAsLive(http, partnerId, { swal });
      await syncPartners();
      if (dossierModalPartnerId && String(dossierModalPartnerId) === String(partnerId)) {
        setDetailRefreshKey((k) => k + 1);
      }
      swal("Success", "Partner set to Live", "success");
    } catch (error) {
      console.error("Error approving partner:", error);
      const errorMessage = error.response?.data?.message || "Approval failed";
      swal("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Fonctions de confirmation
  const confirmApprovePartner = (partner) => {
    setShowConfirmModal({
      isOpen: true,
      type: "approve",
      partner,
      onConfirm: () => {
        approvePartner(partner._id);
        setShowConfirmModal({ isOpen: false, type: null, partner: null, onConfirm: null });
      },
    });
  };

  useEffect(() => {
    const ac = new AbortController();
    const fetchPendingClinics = async () => {
      try {
        const response = await http.get("/api/v1/partners/medical/clinics", {
          params: { status: "pending" },
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        const clinics = response.data?.data || response.data || [];
        const pendingCount = Array.isArray(clinics)
          ? clinics.filter((c) => c.status === "pending" || c.status === "Pending").length
          : 0;
        setClinicsPending(pendingCount);
      } catch (error) {
        if (!isAbortError(error)) console.error("Error loading pending clinics:", error);
        if (!ac.signal.aborted) setClinicsPending(0);
      }
    };
    fetchPendingClinics();
    return () => ac.abort();
  }, [allPartners]);

  const stats = useMemo(() => {
    const total = allPartners.length;
    const pending = allPartners.filter((p) => p.status === "pending" || p.status === "Pending").length;
    const active = allPartners.filter((p) => p.status === "active" || p.status === "Active").length;
    const tcPending = allPartners.filter(
      (p) => String(p.tcApprovalStatus || "").toLowerCase() === "pending"
    ).length;
    const mtEnabled = allPartners.filter((p) => partnerOffersMedicalTourism(p)).length;
    return { total, pending, active, clinicsPending, tcPending, mtEnabled };
  }, [allPartners, clinicsPending]);

  const filterPrimaryUserCandidates = useCallback((u) => filterUserEligibleAsNewMedicalPartnerPrimary(u), []);

  const indexOfLastItem = currentPage * itemPerPage;
  const indexOfFirstItem = indexOfLastItem - itemPerPage;
  const currentItems = filteredPartners.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const onStatCardNavigate = useCallback(
    (key) => {
      setCurrentPage(1);
      if (key === "total") setActiveTab("all");
      else if (key === "pending") setActiveTab("pending");
      else if (key === "active") setActiveTab("active");
      else if (key === "clinicsPending") {
        history.push(`${getPublicUrl("clinics")}?status=pending`);
      }
    },
    [history]
  );

  const runBulkApprovePending = useCallback(async () => {
    const sel = new Set(selectedPartnerIds.map(String));
    const targets = allPartners.filter(
      (p) =>
        p &&
        p._id &&
        sel.has(String(p._id)) &&
        (p.status === "pending" || p.status === "Pending")
    );
    if (!targets.length) {
      swal("No pending partners", "Select rows that are still in Pending workflow.", "info");
      return;
    }
    const ok = await swal({
      title: `Approve ${targets.length} partner(s)?`,
      text: "Sets workflow to Active and links primary users as medical_partner where applicable.",
      icon: "info",
      buttons: ["Cancel", "Approve"],
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      for (const p of targets) {
        try {
          await approveMedicalPartnerAsLive(http, String(p._id), { quietUserWarning: true, swal });
        } catch (e) {
          swal("Stopped", e.response?.data?.message || "Approval failed", "error");
          await syncPartners();
          return;
        }
      }
      await syncPartners();
      if (
        dossierModalPartnerId &&
        targets.some((t) => String(t._id) === String(dossierModalPartnerId))
      ) {
        setDetailRefreshKey((k) => k + 1);
      }
      clearPartnerSelection();
      swal("Success", `${targets.length} partner(s) approved`, "success");
    } finally {
      setBulkBusy(false);
    }
  }, [
    selectedPartnerIds,
    allPartners,
    syncPartners,
    clearPartnerSelection,
    dossierModalPartnerId,
  ]);

  const runBulkSetInactive = useCallback(async () => {
    const sel = new Set(selectedPartnerIds.map(String));
    const targets = allPartners.filter((p) => p && p._id && sel.has(String(p._id)));
    if (!targets.length) return;
    const ok = await swal({
      title: `Set ${targets.length} partner(s) inactive?`,
      text: "Workflow becomes Inactive for every selected row.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Set inactive"],
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      for (const p of targets) {
        await http.put(`/api/v1/medical-partners/${p._id}`, { status: "inactive" });
      }
      await syncPartners();
      if (
        dossierModalPartnerId &&
        targets.some((t) => String(t._id) === String(dossierModalPartnerId))
      ) {
        setDetailRefreshKey((k) => k + 1);
      }
      clearPartnerSelection();
      swal("Success", `${targets.length} partner(s) set to inactive`, "success");
    } catch (e) {
      swal("Error", e.response?.data?.message || "Bulk update failed", "error");
      await syncPartners();
    } finally {
      setBulkBusy(false);
    }
  }, [selectedPartnerIds, allPartners, syncPartners, clearPartnerSelection, dossierModalPartnerId]);

  const selectPartnerRow = useCallback((partnerId, ev) => {
    if (
      ev?.target?.closest?.(
        "a, button, input, select, textarea, .dropdown-menu, .custom-control, .nk-tb-actions, .form-control, .medical-partner-list__select"
      )
    ) {
        return;
      }
    if (partnerId) setDossierModalPartnerId(String(partnerId));
  }, []);

  const openDossierModal = useCallback((partnerId) => {
    if (partnerId) setDossierModalPartnerId(String(partnerId));
  }, []);

  const closeDossierModal = useCallback(() => {
    setDossierModalPartnerId(null);
  }, []);

  return (
    <React.Fragment>
      <Head title="Medical partners" />
      <Content>
        <AdminPageHeader
          title="Medical partners"
          description={
            <p className="mb-0">
              {allPartners.length} partner{allPartners.length !== 1 ? "s" : ""} — open a row or the eye icon for the dossier (profile, clinics,{" "}
              {"TC & MT"}). Draft and pending partners can still configure TC/MT and clinics; setting workflow to Live is verification and should drive
              public visibility (enforce on API and patient site). Checkboxes: bulk activate pending / set inactive. Stats cards filter the list; &quot;Clinics
              pending review&quot; opens the pending clinics queue.
            </p>
          }
          end={<AdminToolbarToggle open={sm} onToggle={() => updateSm(!sm)} />}
        />

        <AdminPageColumn>
          <PartnerStatsCards stats={stats} onCardNavigate={onStatCardNavigate} />

          <MedicalPartnerListTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setCurrentPage={setCurrentPage}
            stats={stats}
          />

          <DataTableShell>
            <MedicalPartnerListToolbar
              onSearchText={onSearchText}
              onFilterChange={onFilterChange}
              filters={filters}
              statusOptions={statusOptions}
              countryOptions={countryOptions}
              tcApprovalOptions={tcApprovalOptions}
              hasListFilters={hasListFilters}
              clearAllListFilters={clearAllListFilters}
              openAddPartnerModal={openAddPartnerModal}
              loading={listUiBusy}
            />
            <MedicalPartnerListBulkBar
              selectedCount={selectedPartnerIds.length}
              busy={bulkBusy}
              onApprovePending={runBulkApprovePending}
              onSetInactive={runBulkSetInactive}
              onClear={clearPartnerSelection}
            />
            <MedicalPartnerListTable
              currentItems={currentItems}
              highlightedPartnerId={dossierModalPartnerId}
              onOpenDossier={openDossierModal}
              selectPartnerRow={selectPartnerRow}
              activeTab={activeTab}
              hasListFilters={hasListFilters}
              openAddPartnerModal={openAddPartnerModal}
              loading={listUiBusy}
              filteredCount={filteredPartners.length}
                  itemPerPage={itemPerPage}
                  currentPage={currentPage}
              paginate={paginate}
              onConfirmDelete={canDeletePartner ? onDeleteClick : null}
              confirmApprovePartner={confirmApprovePartner}
              tcBusyId={tcBusyId}
              onTcSwitchChange={onTcSwitchChange}
              statusBusyId={statusBusyId}
              onStatusSelect={onStatusSelect}
              onMtSwitchChange={onMtSwitchChange}
              onAssignPrimaryUser={(row) => row?._id && setAssignPartnerId(String(row._id))}
              onPatchPartner={patchPartnerListField}
              patchBusyPartnerId={patchBusyPartnerId}
              selectionEnabled
              selectedIds={selectedPartnerIds}
              onToggleRow={togglePartnerRowSelected}
              onToggleAllOnPage={toggleSelectAllOnPage}
            />
          </DataTableShell>
        </AdminPageColumn>

        <Modal isOpen={Boolean(dossierModalPartnerId)} toggle={closeDossierModal} size="xl" className="modal-dialog-centered">
          <ModalBody className="p-3" style={{ maxHeight: "min(90vh, 920px)", overflowY: "auto" }}>
            {dossierModalPartnerId ? (
                  <MedicalPartnerDetails
                key={`${dossierModalPartnerId}-${detailRefreshKey}`}
                detailPartnerId={dossierModalPartnerId}
                onClosePanel={closeDossierModal}
                    refetchPartners={syncPartners}
                  />
            ) : null}
  </ModalBody>
</Modal>

        <MedicalPartnerAssignPrimaryUserModal
          isOpen={Boolean(assignPartnerRecord)}
          partner={assignPartnerRecord}
          onClose={() => setAssignPartnerId(null)}
          onComplete={async () => {
            await syncPartners();
            if (dossierModalPartnerId && assignPartnerId && String(dossierModalPartnerId) === String(assignPartnerId)) {
              setDetailRefreshKey((k) => k + 1);
            }
          }}
        />

        <MedicalPartnerCreateModal
          isOpen={modal.add}
          onClose={() => setModal({ add: false })}
          filterPrimaryUserCandidates={filterPrimaryUserCandidates}
          onPartnersSynced={(cleanData) => setData(cleanData)}
          onOpenDossier={(partnerId) => partnerId && setDossierModalPartnerId(String(partnerId))}
          onBusyChange={setCreatePartnerBusy}
        />

        <ConfirmModal
          isOpen={showConfirmModal.isOpen}
          onClose={() => setShowConfirmModal({ isOpen: false, type: null, partner: null, onConfirm: null })}
          onConfirm={showConfirmModal.onConfirm || (() => {})}
          title={
            showConfirmModal.type === "approve"
              ? "Set partner to Live"
              : showConfirmModal.type === "deletePartner"
              ? "Delete medical partner"
              : "Confirm"
          }
          message={
            showConfirmModal.type === "approve"
              ? `Set "${showConfirmModal.partner?.organization_name || "this partner"}" to Live (active). The primary user is linked as medical_partner where applicable. Public visibility is enforced on API and site.`
              : showConfirmModal.type === "deletePartner"
              ? `Permanently delete "${showConfirmModal.partner?.organization_name || "this partner"}"?\n\nCannot be undone (clinics, catalogue rows, related records).`
              : "Confirm this action?"
          }
          confirmText={
            showConfirmModal.type === "approve"
              ? "Set Live"
              : showConfirmModal.type === "deletePartner"
              ? "Delete"
              : "Confirm"
          }
          cancelText="Cancel"
          type={
            showConfirmModal.type === "approve"
              ? "success"
              : showConfirmModal.type === "deletePartner"
              ? "danger"
              : "warning"
          }
          loading={loading}
        />

        {/* Global error message */}
        {errorVal && (
          <div className="card-inner border-top mt-3">
            <div className="alert alert-danger alert-icon">
              <Icon name="alert-circle" />
              <strong>Error:</strong> {errorVal}
              <button
                type="button"
                className="close"
                onClick={() => setError("")}
                style={{ border: "none", background: "none", fontSize: "20px", cursor: "pointer" }}
              >
                <Icon name="cross" />
              </button>
            </div>
          </div>
        )}
      </Content>
    </React.Fragment>
  );
};
export default MedicalPartner;
